import PageShell from '@/components/Common/PageShell'
import { requirePermission } from '@/utils/auth'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import RouteStatistics from '@/components/RouteStatistics'
import { getRouteStatisticsOptions, getRouteOptions } from '@/api/main'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Title, Grid, Button, Group } from '@mantine/core'
import { TbMap2 } from 'react-icons/tb'
import DrivesTable from '@/components/DrivesTable'
import FragmentsTable from '@/components/FragmentsTable'
import { useValidatedState } from '@mantine/hooks'
import { isEmpty } from 'lodash-es'
import { CITY_NAME } from '@/meta'
import { usePageTitle } from '@/hooks/usePageTitle'
import ErrorState from '@/components/Common/ErrorState'
import { QueryBoundary } from '@/components/Common/QueryBoundary'
import { RouteLoading } from '@/components/Common/RouteLoader'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { useAuthStore } from '@/stores/auth'
import { hasAnyPermission } from '@/utils/permissions'

export const Route = createFileRoute('/route/$routeId/$version')({
  beforeLoad: () => requirePermission('routes'),
  parseParams: params => ({
    routeId: params.routeId,
    version: params.version,
  }),
  component: () => (
    <QueryBoundary
      loadingFallback={<RouteDetailLoadingFallback />}
      errorTitle={i18next.t('failedToLoadRouteDetails', { ns: 'routeManagement' })}
      errorMessage={i18next.t('unableToLoadRouteDetails', { ns: 'routeManagement' })}
      pageTitle={i18next.t('routeDetails', { ns: 'routeManagement' })}
      fullWidth
      backButtonHref="/routes"
    >
      <RouteDetailPage />
    </QueryBoundary>
  ),
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('errorLoadingRouteDetails', { ns: 'routeManagement' })} fullWidth>
      <ErrorState
        title={i18next.t('failedToLoadRouteDetails', { ns: 'routeManagement' })}
        message={error.message || i18next.t('unableToLoadRouteDetails', { ns: 'routeManagement' })}
      />
    </PageShell>
  ),
})

function RouteDetailLoadingFallback() {
  const { routeId } = Route.useParams()
  const { t } = useTranslation('routeManagement')
  return <RouteLoading title={t('routeLoading', { routeId })} fullWidth />
}

function RouteDetailPage() {
  const { routeId, version } = Route.useParams()
  const { t } = useTranslation('routeManagement')
  const navigate = useNavigate()
  const userPermissions = useAuthStore(state => state.userPermissions)
  const canPlan = hasAnyPermission(userPermissions, 'maps') && hasAnyPermission(userPermissions, 'routes')

  usePageTitle(`Route ${routeId} | ${CITY_NAME}`)

  const { data: routeStatistics } = useSuspenseQuery(
    getRouteStatisticsOptions({
      path: {
        route_id: routeId,
        route_version: version,
      },
    })
  )

  const { data: route } = useSuspenseQuery(
    getRouteOptions({
      path: {
        route_id: routeId,
        route_version: version,
      },
    })
  )

  const [{ lastValidValue: selectedFragment }, setSelectedFragment] = useValidatedState<number>(
    0,
    (value: number) => value >= 0 && value < (routeStatistics?.fragments.length || 1)
  )

  const currentSelectedFragment = selectedFragment || 0
  const hasFragments = !isEmpty(routeStatistics?.fragments)

  return (
    <PageShell
      title={route?.name ? `${route.name} | ${routeId}` : routeId}
      fullWidth
      action={
        canPlan && route?.planning_area_id ? (
          <Group>
            <Button
              leftSection={<TbMap2 size={16} />}
              variant="light"
              onClick={() =>
                void navigate({
                  to: '/route-planner/$areaId/$routeId/$version',
                  params: {
                    areaId: route.planning_area_id,
                    routeId,
                    version,
                  },
                })
              }
            >
              {t('plan')}
            </Button>
          </Group>
        ) : undefined
      }
    >
      <RouteStatistics
        routeId={routeId}
        routeVersion={version}
        planningAreaId={route?.planning_area_id}
        routeStatistics={routeStatistics}
        selectedFragment={currentSelectedFragment}
        onSelectedFragmentChange={setSelectedFragment}
      />
      <Grid mt="xl">
        {hasFragments && (
          <Grid.Col span={4}>
            <Title order={4} mb="md">
              {t('fragments')}
            </Title>
            <FragmentsTable
              fragments={routeStatistics.fragments}
              streetSegments={routeStatistics.street_segments}
              routePlan={routeStatistics.route_plan}
              selectedFragment={currentSelectedFragment}
            />
          </Grid.Col>
        )}
        <Grid.Col span={hasFragments ? 8 : 11}>
          <Title order={4} mb="md">
            {t('drives')}
          </Title>
          <DrivesTable driveStatistics={routeStatistics.drive_statistics.filter(d => d.drive_started_at != null)} />
        </Grid.Col>
      </Grid>
    </PageShell>
  )
}
